/**
 * Locale primitives shared by web and mobile.
 *
 * These are the *supported* locales. Which one is the default is deployment
 * configuration (`DEFAULT_LOCALE`) and is still an open question — see
 * docs/open-questions.md Q-07. Nothing in this file assumes an answer.
 */

export const LOCALES = ['en', 'ar'] as const;

export type Locale = (typeof LOCALES)[number];

export type Direction = 'ltr' | 'rtl';

export interface LocaleMeta {
  /** BCP-47 code, used for `lang`, `hreflang` and `Intl`. */
  readonly code: Locale;
  readonly dir: Direction;
  /** Name in English, for admin surfaces and logs. */
  readonly name: string;
  /** Name in its own language, for the public language switcher. */
  readonly nativeName: string;
}

export const LOCALE_META: Readonly<Record<Locale, LocaleMeta>> = {
  en: { code: 'en', dir: 'ltr', name: 'English', nativeName: 'English' },
  ar: { code: 'ar', dir: 'rtl', name: 'Arabic', nativeName: 'العربية' },
};

/**
 * Fallback used when a *message key* is missing from a catalogue.
 *
 * Deliberately distinct from `DEFAULT_LOCALE`: that decides which locale an
 * unprefixed visitor lands on and is environment configuration. This one only
 * decides which catalogue supplies a UI string that a translator has not
 * written yet, and it is never applied to database content — content fallback
 * policy is Q-08 and is resolved by the renderer, not here.
 */
export const MESSAGE_FALLBACK_LOCALE: Locale = 'en';

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

export function directionOf(locale: Locale): Direction {
  return LOCALE_META[locale].dir;
}

export function isRtl(locale: Locale): boolean {
  return LOCALE_META[locale].dir === 'rtl';
}

/**
 * Pick the best supported locale for an `Accept-Language` header.
 *
 * Returns `null` rather than guessing when nothing matches, so the caller
 * applies the configured default explicitly instead of inheriting one here.
 */
export function negotiateLocale(acceptLanguage: string | null | undefined): Locale | null {
  if (!acceptLanguage) return null;

  const ranked = acceptLanguage
    .split(',')
    .map((part) => {
      const [tag = '', ...params] = part.trim().split(';');
      const q = params
        .map((p) => p.trim())
        .find((p) => p.startsWith('q='))
        ?.slice(2);
      const quality = q === undefined ? 1 : Number.parseFloat(q);
      return { tag: tag.trim().toLowerCase(), quality: Number.isNaN(quality) ? 0 : quality };
    })
    .filter((entry) => entry.tag !== '' && entry.quality > 0)
    .sort((a, b) => b.quality - a.quality);

  for (const { tag } of ranked) {
    if (tag === '*') return null;
    const base = tag.split('-')[0];
    if (isLocale(base)) return base;
  }

  return null;
}
